/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, Controller, useCallback, DataTable } from "@";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BanknotesIcon,
  CheckCircleIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";
import type { ComponentType, SVGProps } from "react";
import { banksColumns } from "../TableOptions";
import type { BankAccountsSectionProps } from "./types";
import { BankAccountDisplayOption } from "@/features/donations/projects/types.ts";
import { RepeaterEmpty } from "../RepeaterPrimitives";

interface DisplayOptionDef {
  value: BankAccountDisplayOption;
  label: string;
  hint: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const displayOptions: DisplayOptionDef[] = [
  {
    value: BankAccountDisplayOption.Hidden,
    label: "إخفاء الحسابات",
    hint: "لن تظهر الحسابات البنكية في صفحة المشروع",
    icon: EyeSlashIcon,
  },
  {
    value: BankAccountDisplayOption.Above,
    label: "أعلى الوصف",
    hint: "تظهر الحسابات قبل وصف المشروع",
    icon: ArrowUpIcon,
  },
  {
    value: BankAccountDisplayOption.Below,
    label: "أسفل الوصف",
    hint: "تظهر الحسابات بعد وصف المشروع",
    icon: ArrowDownIcon,
  },
];

export const BankAccountsSection = ({
  show,
  accountsOptions,
  handleSelect,
  control,
  watch,
  getValues,
  checkOnSelectedAccounts,
}: BankAccountsSectionProps) => {
  const bankAccountsIds = watch("bankAccountsIdsArray") || [];
  const showingOption = Number(watch("projectAccountShowingOption"));
  const isHidden = showingOption === BankAccountDisplayOption.Hidden;

  // react-data-table-component fires `onSelectedRowsChange` whenever the
  // `selectableRowSelected` reference changes — which happens on every parent
  // render because `checkOnSelectedAccounts` is recreated each time. That kicks
  // off an infinite loop: setValue → re-render → new fn ref → fires effect →
  // setValue again. Keep the callbacks behind refs so their references stay
  // stable across renders, and bail in handleSelect when the ids haven't
  // actually changed for an extra layer of safety.
  const checkRef = useRef(checkOnSelectedAccounts);
  const handleSelectRef = useRef(handleSelect);
  useEffect(() => {
    checkRef.current = checkOnSelectedAccounts;
    handleSelectRef.current = handleSelect;
  }, [checkOnSelectedAccounts, handleSelect]);

  const stableCheckOnSelectedAccounts = useCallback(
    (row: { id: number }) => checkRef.current(row),
    [],
  );

  const stableHandleSelect = useCallback(
    ({ selectedRows }: { selectedRows: { id: number }[] }) => {
      const newIds = selectedRows.map((r) => r.id);
      const current = (
        (getValues("bankAccountsIds") as number[] | undefined) || []
      ).slice();
      if (
        newIds.length === current.length &&
        newIds.every((id) => current.includes(id))
      ) {
        return;
      }
      handleSelectRef.current({ selectedRows });
    },
    [getValues],
  );

  return (
    <Card
      isOpen={true}
      title={`الحسابات البنكية${
        bankAccountsIds.length > 0 ? ` (${bankAccountsIds.length})` : ""
      }`}
      className="lg:col-span-2"
    >
      {/* Display option selector — visual radio cards */}
      <div className="mb-5">
        <label className="input-label mb-2 block">
          مكان عرض الحسابات في صفحة المشروع
        </label>
        <Controller
          name="projectAccountShowingOption"
          control={control}
          defaultValue={0}
          render={({ field }: { field: any }) => (
            <div className="grid gap-3 sm:grid-cols-3">
              {displayOptions.map((opt) => {
                const Icon = opt.icon;
                const isActive = Number(field.value) === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={!!show}
                    onClick={() => field.onChange(opt.value)}
                    className={`group relative flex items-start gap-3 rounded-2xl border-2 p-4 text-start transition disabled:cursor-not-allowed disabled:opacity-60 ${
                      isActive
                        ? "border-purple-900 bg-purple-50/60 shadow-sm dark:border-purple-400 dark:bg-purple-950/30"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/60 dark:border-gray-700 dark:bg-gray-800/40 dark:hover:border-gray-600"
                    }`}
                  >
                    <span
                      className={`flex size-10 shrink-0 items-center justify-center rounded-2xl transition ${
                        isActive
                          ? "bg-purple-900 text-white dark:bg-purple-500"
                          : "bg-gray-100 text-gray-500 group-hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
                      }`}
                    >
                      <Icon className="size-5" />
                    </span>
                    <span className="flex flex-1 flex-col leading-tight">
                      <span
                        className={`text-sm font-semibold ${
                          isActive
                            ? "text-purple-900 dark:text-purple-300"
                            : "text-gray-800 dark:text-gray-200"
                        }`}
                      >
                        {opt.label}
                      </span>
                      <span className="mt-1 text-[12px] font-normal text-gray-500 dark:text-gray-400">
                        {opt.hint}
                      </span>
                    </span>
                    {isActive && (
                      <span className="absolute end-3 top-3">
                        <CheckCircleIcon className="size-5 text-purple-900 dark:text-purple-400" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        />
      </div>

      {!isHidden && (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800/60">
          {/* Header with selection summary */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/60 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
            <div className="flex items-center gap-3">
              <span className="flex size-9 items-center justify-center rounded-2xl bg-purple-900 text-white dark:bg-purple-500">
                <BanknotesIcon className="size-5" />
              </span>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  حدد الحسابات الظاهرة
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  اختر الحسابات البنكية التي ستظهر للمتبرعين في هذا المشروع
                </p>
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1 ${
                bankAccountsIds.length > 0
                  ? "bg-purple-50 text-purple-900 ring-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800"
                  : "bg-gray-100 text-gray-500 ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700"
              }`}
            >
              <CheckCircleIcon className="size-4" />
              {bankAccountsIds.length} محدد
            </span>
          </div>

          <div className="overflow-hidden rounded-b-2xl">
            {accountsOptions?.length > 0 ? (
              <DataTable
                selectableRows
                disabled={!!show}
                data={accountsOptions}
                columns={banksColumns}
                onSelectedRowsChange={stableHandleSelect}
                noDataComponent="لا توجد سجلات لعرضها"
                selectableRowSelected={stableCheckOnSelectedAccounts}
              />
            ) : (
              <div className="p-3">
                <RepeaterEmpty
                  icon={BanknotesIcon}
                  title="لا توجد حسابات بنكية متاحة"
                  description="يرجى إضافة حسابات بنكية من إعدادات النظام أولاً"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
